import React, { useState, useEffect } from 'react';
import classNames from 'classnames/bind';
import { compose } from 'redux';
import { connect } from 'react-redux';
import {
  Field,
  reduxForm,
  SubmissionError,
  formValueSelector,
} from 'redux-form';
import { Button } from '@beef/ui-kit';

import {
  FORM_TAKEAWAY_NAME,
  TAKEAWAY_FIELD_PHONE,
  TAKEAWAY_FIELD_FIO,
  TAKEAWAY_FIELD_EMAIL,
} from 'store/common/form/constants';
import { RFPhoneInput, RFTextInput } from 'bricks/ReduxForm';
import pushEvent, {
  EVENT_CONTACT_FILLED_PICKUPBUY,
} from 'pushEvent/index';

import { convertNameForGTM, getValidationErrors, toggleBodyStyleForAndroid } from './helpers';
import styles from './styles.pcss';

const cx = classNames.bind(styles);

type IFormData = {
  name: string;
  phone: string;
  email: string;
};

type IStateData = {
  phoneError: string | null,
  nameError: string | null,
  emailError: string | null,
  phoneNumber: string | null
};

type IPickupBuyFormView = {
  reset: () => void;
  formValues: IFormData
  buttonText: string;
  isMobile: boolean;
  handleSubmit: (data: IFormData) => void;
  onClick: (data: IFormData) => void;
  className: string;
};

const PickupBuyFormView = ({
  reset,
  formValues,
  buttonText,
  isMobile,
  handleSubmit,
  onClick,
  className,
}: IPickupBuyFormView) => {
  const [stateObj, setStateObj] = useState<IStateData>({
    phoneError: null,
    nameError: null,
    emailError: null,
    phoneNumber: null,
  });

  useEffect(() => {
    toggleBodyStyleForAndroid();
  }, []);

  const handleValidate = (value, allValues, props) => {
    let stateObject = {
      phoneError: null,
      nameError: null,
      emailError: null,
      phoneNumber: stateObj.phoneNumber,
    };
    try {
      const validationErrors = getValidationErrors(allValues, props);
      /**
       * if we got an error, throw it for every field separately
       */
      if (Object.keys(validationErrors).length) {
        throw new SubmissionError({ ...validationErrors });
      }
    } catch (e) {
      const { errors } = e;
      const { name, phone, email } = formValues;
      stateObject = {
        phoneError: (phone && errors) && errors[TAKEAWAY_FIELD_PHONE] ? errors[TAKEAWAY_FIELD_PHONE] : null,
        nameError: (name && errors) && errors[TAKEAWAY_FIELD_FIO] ? errors[TAKEAWAY_FIELD_FIO] : null,
        emailError: (email && errors) && errors[TAKEAWAY_FIELD_EMAIL] ? errors[TAKEAWAY_FIELD_EMAIL] : null,
        phoneNumber: stateObj.phoneNumber,
      };
    }
    setStateObj(stateObject);
  };

  const handleBlur = (event, value, previousValue, name) => {
    if (value && !stateObj[`${name}Error`]) {
      pushEvent(EVENT_CONTACT_FILLED_PICKUPBUY, { fieldData: convertNameForGTM(name) });
    }
  };

  const buttonDisabled = () => {
    const { name, phone } = formValues;
    const { phoneError, nameError, emailError } = stateObj;
    return !!(!name || !phone || phoneError || nameError || emailError);
  };

  const scrollToField = (event, name) => {
    if (/Android/.test(navigator.appVersion)) {
      const inputField = document.getElementsByName(`${name}`)[0];
      if (inputField) {
        inputField.scrollIntoView();
      }
    }
  };
  const onSetState = (event) => {
    setStateObj({ ...stateObj, phoneNumber: event.target.value });
  };

  return (
    <div className={cx(['wrapper', className])} id="pickup-buy-node">
      { /* eslint-disable-next-line no-unused-expressions */}
      <form onSubmit={handleSubmit(data => { onClick(data); reset(); })}>
        <div className={cx('fields', isMobile && 'mobile')}>
          <div className={cx('block', isMobile && 'mobile')}>
            <div className={cx('label')}>Телефон*</div>
            <div className={cx('input', isMobile && 'mobile')}>
              <Field
                name={TAKEAWAY_FIELD_PHONE}
                component={RFPhoneInput}
                placeholder=""
                normalize={value => value.replace(/(^\+7|\s)/g, '')}
                validate={handleValidate}
                onChange={onSetState}
                onBlur={handleBlur}
                onFocus={scrollToField}
                value={stateObj.phoneNumber}
              />
              <div className={cx('error')}>{stateObj.phoneError}</div>
            </div>
          </div>
          <div className={cx('block', isMobile && 'mobile')}>
            <div className={cx('label')}>Имя*</div>
            <div className={cx('input', isMobile && 'mobile')}>
              <Field
                name={TAKEAWAY_FIELD_FIO}
                onBlur={handleBlur}
                component={RFTextInput}
                validate={handleValidate}
                placeholder=""
                type="text"
                onFocus={scrollToField}
              />
              <div className={cx('error')}>{stateObj.nameError}</div>
            </div>
          </div>
          <div className={cx('block', isMobile && 'mobile')}>
            <div className={cx('label')}>Email</div>
            <div className={cx('input', isMobile && 'mobile')}>
              <Field
                name={TAKEAWAY_FIELD_EMAIL}
                component={RFTextInput}
                onBlur={handleBlur}
                placeholder=""
                onFocus={scrollToField}
              />
              <div className={cx('error')}>{stateObj.emailError}</div>
            </div>
          </div>
        </div>
        <div className={cx('button', isMobile && 'mobile')}>
          <Button
            fullWidth
            size="l"
            type="submit"
            className={!isMobile ? 'big' : null}
            disabled={buttonDisabled()}
          >
            {buttonText}
          </Button>
        </div>
      </form>
    </div>
  );
};

const selector = formValueSelector(FORM_TAKEAWAY_NAME);

export default compose(
  connect(state => ({
    formValues: selector(
      state,
      TAKEAWAY_FIELD_PHONE,
      TAKEAWAY_FIELD_FIO,
      TAKEAWAY_FIELD_EMAIL,
    ),
  })),
  reduxForm({
    form: FORM_TAKEAWAY_NAME,
    destroyOnUnmount: false,
    touchOnBlur: true,
  }),
)(PickupBuyFormView);
